<template>
  <div class="horizontal-menu-wrapper">
    <div id="horizontal-menu" :class="{ 'has-test-mode': TestMode }">
      <v-overlay :value="OverlayLoadingFlag" class="custom-nav-overlay">
        <v-progress-circular indeterminate size="64" color="#0057A8" />
      </v-overlay>

      <!-- Executive Top Bar Container -->
      <div class="horizontal-menu-container">
        <!-- Ambient Background Light Sweep Effect -->
        <div class="nav-ambient-glow" aria-hidden="true" />

        <!-- Mobile Menu Toggle Button -->
        <button
          class="mobile-menu-toggle"
          aria-label="Toggle navigation menu"
          @click="toggleMobileMenu"
        >
          <v-icon color="#0f172a">
            {{ mobileMenuOpen ? "mdi-close" : "mdi-menu" }}
          </v-icon>
        </button>

        <div class="left-section">
          <!-- Role-Specific Header Identity Badge -->
          <div class="role-indicator">
            <div v-if="RoleId == 175" class="national-indicator identity-badge">
              <span class="pulse-dot" />
              <v-icon small class="badge-icon mr-1.5">
                mdi-shield-crown-outline
              </v-icon>
              <span class="badge-text">National Awards</span>
            </div>
            <div v-else class="zone-indicator identity-badge">
              <span class="pulse-dot" />
              <v-icon small class="badge-icon mr-1.5">
                mdi-map-marker-radius-outline
              </v-icon>
              <span class="badge-text">{{ ZoneDisplayText }}</span>
            </div>
          </div>

          <!-- Horizontal Menu Navigation - Desktop Floating Executive Rail -->
          <nav class="horizontal-nav desktop-nav">
            <!-- <div
              v-if="!isJudgeOnly"
              class="menu-dropdown dashboard-nav-item"
              :class="{
                'is-active':
                  $route.path === '/dashboard' || $route.path === '/home',
              }"
              @click="$router.push('/dashboard')"
            >
              <div class="dropdown-trigger">
                <div class="nav-glow-bg" />
                <v-icon small class="section-icon mr-2.5"
                  >mdi-view-dashboard-outline</v-icon
                >
                <span class="section-title">Dashboard</span>
              </div>
            </div> -->

            <div
              v-for="section in menuSections"
              :key="section.id"
              class="menu-dropdown"
              :class="{
                'is-active': isSectionActive(section),
                'is-open': activeDropdownId === section.id,
              }"
            >
              <div
                class="dropdown-trigger"
                @click.stop="toggleDropdown(section.id)"
              >
                <div class="nav-glow-bg" />
                <v-icon small class="section-icon mr-2.5">
                  {{ getSectionIcon(section.title) }}
                </v-icon>
                <span class="section-title">{{ section.title }}</span>
                <v-icon small class="chevron-icon ml-1.5">
                  mdi-chevron-down
                </v-icon>
                <span class="nav-underline" />
              </div>

              <div class="dropdown-content">
                <div class="dropdown-connector" />
                <div class="dropdown-top-glow" />
                <router-link
                  v-for="(item, index) in section.items"
                  :key="`item-${item.id}`"
                  :to="item.route"
                  class="menu-item"
                  :style="{ '--item-index': index }"
                  @click.native="activeDropdownId = null"
                >
                  <div class="item-left-indicator" />
                  <div class="item-icon-wrapper">
                    <v-icon size="18px">
                      {{ item.icon }}
                    </v-icon>
                  </div>
                  <div class="item-text-group">
                    <span class="item-title">{{ item.title }}</span>
                  </div>
                </router-link>
              </div>
            </div>
          </nav>
        </div>

        <!-- Mobile Menu - Floating Accordion Panel -->
        <div class="mobile-menu" :class="{ active: mobileMenuOpen }">
          <div class="mobile-menu-inner">
            <!-- <div
              v-if="!isJudgeOnly"
              class="mobile-dropdown"
              @click="
                $router.push('/dashboard');
                closeMobileMenu();
              "
            >
              <div class="mobile-section-title">
                <div class="mobile-title-left">
                  <v-icon small color="#0057a8" class="mr-2">
                    mdi-view-dashboard-outline
                  </v-icon>
                  <span>Dashboard</span>
                </div>
              </div>
            </div> -->

            <div
              v-for="section in menuSections"
              :key="`mobile-${section.id}`"
              class="mobile-dropdown"
            >
              <div
                class="mobile-section-title"
                @click="toggleSection(section.id)"
              >
                <div class="mobile-title-left">
                  <v-icon small color="#0057a8" class="mr-2">
                    {{ getSectionIcon(section.title) }}
                  </v-icon>
                  <span>{{ section.title }}</span>
                </div>
                <v-icon small class="mobile-chevron">
                  {{
                    expandedSections.includes(section.id)
                      ? "mdi-chevron-up"
                      : "mdi-chevron-down"
                  }}
                </v-icon>
              </div>

              <div
                class="mobile-section-content"
                :class="{ expanded: expandedSections.includes(section.id) }"
              >
                <router-link
                  v-for="item in section.items"
                  :key="`mobile-item-${item.id}`"
                  :to="item.route"
                  class="mobile-menu-item"
                  @click="closeMobileMenu"
                >
                  <v-icon size="18px">
                    {{ item.icon }}
                  </v-icon>
                  <span>{{ item.title }}</span>
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- User Profile Section Pod with Dropdown Menu -->
        <div class="user-section">
          <v-menu
            v-model="profileMenuOpen"
            offset-y
            bottom
            left
            transition="slide-y-transition"
            rounded="lg"
            content-class="profile-menu-dropdown-content"
          >
            <template #activator="{ props }">
              <v-btn text class="profile-pod text-none" v-bind="props">
                <div class="user-avatar">
                  <v-avatar size="36" class="avatar-box">
                    <img
                      v-if="ProfileImagePath"
                      :src="ProfileImagePath"
                      alt="User Profile"
                      class="profile-image"
                      @error="handleImageError"
                    />
                    <span v-else class="initials-text">{{
                      getInitials(DisplayName)
                    }}</span>
                  </v-avatar>
                  <span class="status-online-dot"
                    ><span class="status-ping"
                  /></span>
                </div>

                <div class="user-info d-flex align-center">
                  <span class="user-id mr-1" :title="DisplayName">{{
                    DisplayName
                  }}</span>
                  <v-icon
                    small
                    color="#64748b"
                    class="profile-chevron-icon"
                    :class="{ 'rotate-180': profileMenuOpen }"
                  >
                    mdi-chevron-down
                  </v-icon>
                </div>
              </v-btn>
            </template>

            <v-list
              dense
              class="py-2 rounded-lg elevation-8"
              style="min-width: 190px; background: #ffffff"
            >
              <v-list-item
                link
                class="px-4 py-1"
                @click="openChangePasswordModal"
              >
                <v-list-item-icon class="mr-3 my-0 align-self-center">
                  <v-icon small color="#2c63ac"> mdi-key-change </v-icon>
                </v-list-item-icon>
                <v-list-item-title
                  class="font-weight-bold grey--text text--darken-3 text-body-2"
                >
                  Change Password
                </v-list-item-title>
              </v-list-item>

              <v-divider class="my-1" />

              <v-list-item link class="px-4 py-1" @click="UserSessionClear">
                <v-list-item-icon class="mr-3 my-0 align-self-center">
                  <v-icon small color="#e11d48"> mdi-logout-variant </v-icon>
                </v-list-item-icon>
                <v-list-item-title
                  class="font-weight-bold red--text text--darken-1 text-body-2"
                >
                  Logout
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </div>

    <!-- Layout Spacer for Fixed Navigation Bar -->
    <div
      class="nav-fixed-spacer"
      :class="{ 'has-test-mode': TestMode }"
      aria-hidden="true"
    />

    <!-- Change Password Dialog Modal -->
    <v-dialog
      v-model="changePasswordDialog"
      max-width="460"
      persistent
      overlay-color="#0f172a"
      overlay-opacity="0.5"
    >
      <v-card class="change-password-card rounded-xl">
        <v-card-title
          class="dialog-header px-6 py-4 d-flex align-center justify-space-between"
        >
          <div class="d-flex align-center">
            <div class="header-icon-box mr-3">
              <v-icon color="#0057a8" size="22">
                mdi-shield-key-outline
              </v-icon>
            </div>
            <div>
              <h3 class="dialog-title text-h6 font-weight-bold mb-0">
                Change Password
              </h3>
              <span class="dialog-subtitle text-caption text--secondary"
                >Update your account credentials</span
              >
            </div>
          </div>
          <v-btn icon small class="close-btn" @click="closeChangePasswordModal">
            <v-icon small> mdi-close </v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="px-6 py-5">
          <v-form
            ref="changePasswordForm"
            v-model="formValid"
            lazy-validation
            @submit.prevent="submitChangePassword"
          >
            <div class="mb-4">
              <label
                class="form-field-label text-caption font-weight-bold grey--text text--darken-2 d-block mb-1"
              >
                Current Password <span class="red--text">*</span>
              </label>
              <v-text-field
                v-model="passwordForm.CurrentPassword"
                :type="showCurrentPassword ? 'text' : 'password'"
                :append-icon="showCurrentPassword ? 'mdi-eye-off' : 'mdi-eye'"
                placeholder="Enter current password"
                outlined
                dense
                hide-details="auto"
                class="custom-input-field"
                :rules="[rules.required]"
                @click:append="showCurrentPassword = !showCurrentPassword"
              />
            </div>

            <div class="mb-4">
              <label
                class="form-field-label text-caption font-weight-bold grey--text text--darken-2 d-block mb-1"
              >
                New Password <span class="red--text">*</span>
              </label>
              <v-text-field
                v-model="passwordForm.NewPassword"
                :type="showNewPassword ? 'text' : 'password'"
                :append-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
                placeholder="Enter new password (min 6 characters)"
                outlined
                dense
                hide-details="auto"
                class="custom-input-field"
                :rules="[rules.required, rules.minSix, rules.maxTwenty]"
                @click:append="showNewPassword = !showNewPassword"
              />
            </div>

            <div class="mb-2">
              <label
                class="form-field-label text-caption font-weight-bold grey--text text--darken-2 d-block mb-1"
              >
                Confirm New Password <span class="red--text">*</span>
              </label>
              <v-text-field
                v-model="passwordForm.ConfirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                :append-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
                placeholder="Confirm new password"
                outlined
                dense
                hide-details="auto"
                class="custom-input-field"
                :rules="[rules.required, rules.passwordMatch]"
                @click:append="showConfirmPassword = !showConfirmPassword"
              />
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-6 pt-2 d-flex align-center justify-end">
          <v-btn
            outlined
            color="grey darken-1"
            class="px-5 text-capitalize rounded-lg"
            :disabled="passwordLoading"
            @click="closeChangePasswordModal"
          >
            Cancel
          </v-btn>
          <v-btn
            color="#0057a8"
            class="px-6 text-capitalize rounded-lg white--text ml-3 font-weight-bold"
            elevation="2"
            :loading="passwordLoading"
            @click="submitChangePassword"
          >
            Update Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/Common.vue";
import companyConfig from "@/company_config.json";

export default {
  name: "HorizontalMenu",
  components: {},
  mixins: [common],
  data() {
    return {
      RoleId: "",
      Logo: "",
      ZoneDisplayText: "",
      ProfileImagePath: "",
      DisplayName: "",
      OverlayLoadingFlag: false,
      mobileMenuOpen: false,
      profileMenuOpen: false,
      activeDropdownId: null,
      expandedSections: [], // Track which sections are expanded in mobile view
      windowWidth: window.innerWidth, // Track window width for responsive behavior

      JudgeFlag: false,
      HeadTableFlag: false,

      // Menu structure with role-based visibility
      menuSections: [],
      TestMode: companyConfig.TestMode || false,

      // Change Password Dialog state
      changePasswordDialog: false,
      passwordLoading: false,
      formValid: true,
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      passwordForm: {
        CurrentPassword: "",
        NewPassword: "",
        ConfirmPassword: "",
      },
      rules: {
        required: (v) => !!v || "Field is required",
        minSix: (v) =>
          (v && v.length >= 6) || "Password must be at least 6 characters",
        maxTwenty: (v) =>
          (v && v.length <= 20) || "Password must not exceed 20 characters",
        passwordMatch: (v) =>
          v === this.passwordForm.NewPassword || "Passwords do not match",
      },
    };
  },
  computed: {
    isJudgeOnly() {
      const user = this.$session.get("user") || {};
      const judgeFlag = user.JudgeFlag || this.RoleId == 173;
      const isPureJudge =
        judgeFlag &&
        !user.ElectionOfficerFlag &&
        !user.NationalNominationCommitteeChairmanFlag &&
        !user.HeadTableFlag &&
        !user.CommitteeChairmanFlag &&
        !user.CommitteeMemberFlag;
      return isPureJudge;
    },
  },
  watch: {
    $route() {
      this.autoExpandActiveSection();
    },
    menuSections: {
      handler() {
        this.autoExpandActiveSection();
      },
      deep: true,
      immediate: true,
    },
  },
  beforeMount() {
    var roleId = this.$session.get("RoleId");
    roleId = roleId == (null || undefined) ? "" : roleId;
    this.RoleId = roleId;

    var JudgeFlag = roleId == 173 ? true : false;
    console.log({ JudgeFlag });
    this.JudgeFlag = JudgeFlag;
    this.HeadTableFlag = JudgeFlag ? false : true;

    var zoneNameTxt = this.$session.get("ZoneDisplayText");
    zoneNameTxt = zoneNameTxt == (null || undefined) ? "" : zoneNameTxt;
    console.log({ zoneNameTxt });
    this.ZoneDisplayText = zoneNameTxt;

    var server_url = companyConfig.apiURL;
    this.Logo = server_url + "assets/images/logo.png";

    console.log("Loading user profile");
    var user = this.$session.get("user");
    console.log({ user });
    user = user == (null || undefined) ? {} : user;
    console.log({ user });

    var DisplayName = Object.prototype.hasOwnProperty.call(user, "Username")
      ? user.Username
      : "";
    console.log({ DisplayName });
    this.DisplayName = DisplayName;

    var MemberImage = Object.prototype.hasOwnProperty.call(user, "MemberImage")
      ? user.MemberImage
      : "";
    console.log({ MemberImage });

    if (MemberImage && MemberImage.trim() !== "") {
      if (
        !MemberImage.startsWith("http://") &&
        !MemberImage.startsWith("https://")
      ) {
        this.ProfileImagePath =
          companyConfig.apiURL + MemberImage.replace(/^\//, "");
      } else {
        this.ProfileImagePath = MemberImage;
      }
    } else {
      this.ProfileImagePath =
        companyConfig.apiURL + "assets/images/default-profile.png";
    }

    this.fetchMenuData();
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("click", this.handleOutsideClick);
  },
  methods: {
    toggleDropdown(sectionId) {
      if (this.activeDropdownId === sectionId) {
        this.activeDropdownId = null;
      } else {
        this.activeDropdownId = sectionId;
      }
    },
    isRouteActive(route) {
      if (!route || !this.$route) return false;
      const currentPath = this.$route.path;
      return currentPath === route || currentPath.startsWith(route + "/");
    },
    isSectionActive(section) {
      if (!section) return false;
      if (this.activeDropdownId === section.id) return true;
      if (
        this.activeDropdownId === null &&
        section.items &&
        Array.isArray(section.items)
      ) {
        return section.items.some(
          (item) => item.route && this.isRouteActive(item.route)
        );
      }
      return false;
    },
    autoExpandActiveSection() {
      if (!this.menuSections || !Array.isArray(this.menuSections)) return;
      this.menuSections.forEach((section) => {
        if (
          section.items &&
          section.items.some(
            (item) => item.route && this.isRouteActive(item.route)
          )
        ) {
          if (!this.expandedSections.includes(section.id)) {
            this.expandedSections.push(section.id);
          }
        }
      });
    },
    handleOutsideClick(event) {
      const navElement = this.$el
        ? this.$el.querySelector(".desktop-nav")
        : null;
      if (navElement && !navElement.contains(event.target)) {
        this.activeDropdownId = null;
      }
    },
    toggleProfileMenu() {
      this.profileMenuOpen = !this.profileMenuOpen;
    },
    getSectionIcon(title) {
      if (!title) return "mdi-folder-outline";
      const t = title.toLowerCase();
      if (t.includes("zone")) return "mdi-map-marker-radius-outline";
      if (t.includes("application") || t.includes("nomination"))
        return "mdi-file-document-multiple-outline";
      if (t.includes("zp") && !t.includes("zvp"))
        return "mdi-account-group-outline";
      if (t.includes("zvp")) return "mdi-account-star-outline";
      if (t.includes("election") || t.includes("result") || t.includes("vote"))
        return "mdi-poll";
      if (t.includes("judge")) return "mdi-gavel";
      if (t.includes("award") && t.includes("mapping"))
        return "mdi-sitemap-outline";
      if (t.includes("award")) return "mdi-trophy-outline";
      if (t.includes("dash")) return "mdi-view-dashboard-outline";
      if (t.includes("report")) return "mdi-chart-bar";
      return "mdi-folder-outline";
    },
    getInitials(name) {
      if (!name) return "";
      return name
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase())
        .join("")
        .substring(0, 2);
    },
    handleImageError() {
      console.log("Profile image failed to load, using initials instead");
      this.ProfileImagePath = null;
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
      if (!this.mobileMenuOpen) {
        this.expandedSections = [];
      }
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
      this.expandedSections = [];
    },
    toggleSection(sectionId) {
      const index = this.expandedSections.indexOf(sectionId);
      if (index === -1) {
        this.expandedSections.push(sectionId);
      } else {
        this.expandedSections.splice(index, 1);
      }
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
      if (this.windowWidth > 768 && this.mobileMenuOpen) {
        this.closeMobileMenu();
      }
    },
    fetchMenuData() {
      console.log("Fetching menu data from API");
      this.OverlayLoadingFlag = true;

      console.log("RoleId=" + this.RoleId);
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/awards/menu-lists";
      var password =
        this.$session.get("Password") || localStorage.getItem("Password") || "";
      var upload = {
        UserInterface: 1,
        RoleId: this.RoleId,
        Password: password,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("Menu API response:", response.data);
          thisIns.OverlayLoadingFlag = false;

          const flag = response.data.flag;

          if (
            flag == 1 &&
            response.data.records &&
            response.data.records.length > 0
          ) {
            const rawSections = response.data.records || [];
            thisIns.menuSections = rawSections.filter(
              (sec) =>
                sec && sec.title && sec.title.toLowerCase() !== "dashboard"
            );
            console.log("Menu sections loaded from API:", thisIns.menuSections);
          } else {
            console.error("Failed to load menu data or empty response");
          }
        })
        .catch(function (error) {
          console.error("Error fetching menu data:", error);
          thisIns.OverlayLoadingFlag = false;
        });
    },
    UserSessionClear() {
      console.log("UserSessionClear called");

      var role_id = this.$session.get("RoleId");
      role_id = role_id == (null || undefined) ? 0 : role_id;

      if (role_id > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/users/clear-session";
        var password =
          this.$session.get("Password") ||
          localStorage.getItem("Password") ||
          "";
        var upload = {
          UserInterface: 2,
          Password: password,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var error = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.OverlayLoadingFlag = false;

            flag = response.data.flag;
            output = response.data.output;
            error = response.data.error;
            records = response.data.records;
            console.log({ flag }, { output }, { error }, { records });

            if (flag == 1) {
              thisIns.LogoutUser();
            } else {
              if (parseInt(error) == 401) {
                thisIns.LogoutUser();
              }
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      }
    },
    openChangePasswordModal() {
      this.passwordForm = {
        CurrentPassword: "",
        NewPassword: "",
        ConfirmPassword: "",
      };
      this.showCurrentPassword = false;
      this.showNewPassword = false;
      this.showConfirmPassword = false;
      this.changePasswordDialog = true;
      this.$nextTick(() => {
        if (this.$refs.changePasswordForm) {
          this.$refs.changePasswordForm.resetValidation();
        }
      });
    },
    closeChangePasswordModal() {
      this.changePasswordDialog = false;
      this.passwordLoading = false;
    },
    submitChangePassword() {
      if (
        this.$refs.changePasswordForm &&
        !this.$refs.changePasswordForm.validate()
      ) {
        return;
      }
      this.passwordLoading = true;
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/awards/change-password";
      var upload = {
        UserInterface: 1,
        CurrentPassword: this.passwordForm.CurrentPassword,
        NewPassword: this.passwordForm.NewPassword,
        ConfirmPassword: this.passwordForm.ConfirmPassword,
      };

      const thisIns = this;
      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.passwordLoading = false;
          const flag = response.data.flag;
          const output =
            response.data.output ||
            response.data.error ||
            "Password update response received.";
          if (flag == 1 || flag == true) {
            thisIns.$session.set(
              "Password",
              thisIns.passwordForm.NewPassword,
              false
            );
            try {
              sessionStorage.setItem(
                "Password",
                JSON.stringify(thisIns.passwordForm.NewPassword)
              );
              localStorage.setItem(
                "Password",
                thisIns.passwordForm.NewPassword
              );
            } catch (e) {
              // fallback
            }
            thisIns.closeChangePasswordModal();
            thisIns.sweetAlert("success", output, true);
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          thisIns.passwordLoading = false;
          console.error("Error changing password:", error);
          thisIns.sweetAlert(
            "error",
            "Failed to change password. Please try again.",
            false
          );
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap");

/* Keyframe Animations */
@keyframes auroraSweep {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes statusPulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

@keyframes itemCascade {
  0% {
    opacity: 0;
    transform: translateY(6px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.horizontal-menu-wrapper {
  width: 100%;
}

.nav-fixed-spacer {
  height: 64px;
  width: 100%;
  visibility: hidden;
  pointer-events: none;

  &.has-test-mode {
    height: 89px; /* 64px nav menu + 25px test mode fixed banner */
  }

  @media (max-width: 860px) {
    height: 56px;

    &.has-test-mode {
      height: 81px; /* 56px nav menu + 25px test mode fixed banner */
    }
  }
}

#horizontal-menu {
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 0;

  &.has-test-mode {
    top: 25px; /* Ensures DemoMode 25px fixed header is never hidden when scrolling */
  }

  .custom-nav-overlay {
    z-index: 999;
    .v-progress-circular {
      color: #0057a8 !important;
    }
  }

  /* ---------------- FULL-WIDTH EXECUTIVE AURORA TOP BAR ---------------- */
  .horizontal-menu-container {
    font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont,
      sans-serif;
    position: relative;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(226, 232, 240, 0.85);
    box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05),
      0 1px 3px rgba(15, 23, 42, 0.03);
    padding: 0 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 64px;
    width: 100%;
    overflow: visible;
    transition: all 0.3s ease;

    @media (max-width: 860px) {
      padding: 0 16px;
      height: 56px;
    }
  }

  /* Top Animated Energy Sweep Accent Line */
  .nav-ambient-glow {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      #0057a8 0%,
      #0284c7 25%,
      #38bdf8 50%,
      #6366f1 75%,
      #0057a8 100%
    );
    background-size: 200% 100%;
    animation: auroraSweep 8s linear infinite;
    pointer-events: none;
    z-index: 10;
  }

  /* Mobile menu toggle button */
  .mobile-menu-toggle {
    display: none;
    background: rgba(241, 245, 249, 0.9);
    border: 1px solid rgba(203, 213, 225, 0.8);
    border-radius: 10px;
    width: 38px;
    height: 38px;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    z-index: 2;

    &:hover {
      background: #e2e8f0;
    }

    @media (max-width: 860px) {
      display: flex;
    }
  }

  .left-section {
    display: flex;
    align-items: center;
    gap: 20px;
    height: 100%;
    z-index: 1;

    @media (max-width: 860px) {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      height: 100%;
    }
  }

  /* ---------------- ROLE INDICATOR IDENTITY MARKER ---------------- */
  .role-indicator {
    display: flex;
    align-items: center;

    .identity-badge {
      position: relative;
      display: inline-flex;
      align-items: center;
      background: rgba(248, 250, 252, 0.85);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(226, 232, 240, 0.9);
      border-radius: 20px;
      padding: 5px 12px;
      box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
      transition: all 0.25s ease;

      &:hover {
        border-color: rgba(0, 87, 168, 0.3);
        box-shadow: 0 4px 12px rgba(0, 87, 168, 0.08);
      }

      .pulse-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #10b981;
        margin-right: 8px;
        animation: statusPulse 2s infinite;
        flex-shrink: 0;
      }

      .badge-icon {
        color: #0057a8 !important;
        font-size: 15px !important;
      }

      .badge-text {
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.7px;
        color: #0f172a;
        text-transform: uppercase;
        white-space: nowrap;
      }
    }
  }

  /* ---------------- DESKTOP FLOATING COMMAND RAIL ---------------- */
  .horizontal-nav {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 100%;

    &.desktop-nav {
      @media (max-width: 860px) {
        display: none;
      }
    }

    .menu-dropdown {
      position: relative;
      height: 100%;
      display: flex;
      align-items: center;

      .dropdown-trigger {
        position: relative;
        display: flex;
        align-items: center;
        padding: 8px 14px;
        height: 40px;
        border-radius: 10px;
        background: transparent; /* COMPLETELY REMOVED BOXED/CARD CONTAINER */
        border: none;
        cursor: pointer;
        color: #475569;
        font-size: 13.5px;
        font-weight: 600;
        letter-spacing: 0.15px;
        transition: color 0.25s ease, transform 0.25s ease;

        .nav-glow-bg {
          position: absolute;
          inset: 0;
          border-radius: 10px;
          background: radial-gradient(
            circle at center,
            rgba(0, 87, 168, 0.07) 0%,
            transparent 75%
          );
          opacity: 0;
          transform: scale(0.85);
          transition: opacity 0.3s ease, transform 0.3s ease;
          pointer-events: none;
        }

        .section-icon {
          color: #64748b !important;
          font-size: 18px !important;
          margin-right: 10px !important;
          transition: color 0.25s ease,
            transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), filter 0.25s ease;
        }

        .section-title {
          transition: color 0.25s ease, font-weight 0.25s ease;
        }

        .chevron-icon {
          color: #94a3b8 !important;
          font-size: 16px !important;
          margin-left: 6px !important;
          transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
            color 0.25s ease;
        }

        .nav-underline {
          position: absolute;
          bottom: 4px;
          left: 14px;
          right: 14px;
          height: 2px;
          background: linear-gradient(90deg, #0057a8 0%, #38bdf8 100%);
          border-radius: 2px;
          box-shadow: 0 1px 6px rgba(0, 87, 168, 0.4);
          transform: scaleX(0);
          transform-origin: center;
          transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
      }

      /* Active Link Header Highlight State */
      &.is-active {
        .dropdown-trigger {
          color: #0057a8;
          font-weight: 700;

          .section-icon {
            color: #0057a8 !important;
            filter: drop-shadow(0 2px 6px rgba(0, 87, 168, 0.3));
          }

          .nav-underline {
            transform: scaleX(1);
            height: 2.5px;
            background: linear-gradient(90deg, #0057a8, #0284c7);
          }
        }
      }

      /* Hover & Click Open Interaction for Dropdown Popover */
      &:hover,
      &.is-open {
        .dropdown-trigger {
          color: #0057a8;

          .nav-glow-bg {
            opacity: 1;
            transform: scale(1);
          }

          .section-icon {
            color: #0057a8 !important;
            transform: scale(1.12) translateY(-1.5px);
            filter: drop-shadow(0 2px 5px rgba(0, 87, 168, 0.25));
          }

          .chevron-icon {
            transform: rotate(180deg);
            color: #0057a8 !important;
          }

          .nav-underline {
            transform: scaleX(1);
          }
        }

        .dropdown-content {
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
          transform: translateX(-50%) translateY(0) scale(1);

          .menu-item {
            animation-delay: calc(var(--item-index, 0) * 45ms);
          }
        }
      }

      /* Active Link Matching State */
      &:has(.router-link-active) {
        .dropdown-trigger {
          color: #0057a8;
          font-weight: 700;

          .section-icon {
            color: #0057a8 !important;
            filter: drop-shadow(0 2px 6px rgba(0, 87, 168, 0.3));
          }

          .nav-underline {
            transform: scaleX(1);
            height: 2.5px;
            background: linear-gradient(90deg, #0057a8, #0284c7);
          }
        }
      }

      /* ---------------- FLOATING COMMAND DROPDOWN PANEL ---------------- */
      .dropdown-content {
        position: absolute;
        top: calc(100% + 6px);
        left: 50%;
        transform: translateX(-50%) translateY(-8px) scale(0.97);
        min-width: 240px;
        background: rgba(255, 255, 255, 0.97);
        backdrop-filter: blur(20px);
        -webkit-backdrop-filter: blur(20px);
        border: 1px solid rgba(226, 232, 240, 0.85);
        border-radius: 16px;
        box-shadow: 0 20px 40px -8px rgba(15, 23, 42, 0.12),
          0 4px 12px -2px rgba(15, 23, 42, 0.04),
          0 0 0 1px rgba(255, 255, 255, 0.6) inset;
        z-index: 1000;
        padding: 8px;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition: opacity 0.28s cubic-bezier(0.16, 1, 0.3, 1),
          transform 0.28s cubic-bezier(0.16, 1, 0.3, 1),
          visibility 0.28s cubic-bezier(0.16, 1, 0.3, 1);
        transform-origin: top center;

        /* Floating Arrow Caret */
        .dropdown-connector {
          position: absolute;
          top: -6px;
          left: 50%;
          transform: translateX(-50%) rotate(45deg);
          width: 12px;
          height: 12px;
          background: #ffffff;
          border-top: 1px solid rgba(226, 232, 240, 0.85);
          border-left: 1px solid rgba(226, 232, 240, 0.85);
          border-radius: 2px 0 0 0;
        }

        .dropdown-top-glow {
          position: absolute;
          top: 0;
          left: 16px;
          right: 16px;
          height: 2px;
          background: linear-gradient(
            90deg,
            transparent 0%,
            #0057a8 50%,
            transparent 100%
          );
          border-radius: 2px;
          opacity: 0.6;
        }

        .menu-item {
          position: relative;
          display: flex;
          align-items: center;
          padding: 10px 14px;
          margin: 3px 0;
          border-radius: 10px;
          text-decoration: none;
          color: #334155;
          transition: background 0.2s ease, color 0.2s ease,
            transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          opacity: 0;
          transform: translateY(4px);
          animation: itemCascade 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;

          .item-left-indicator {
            position: absolute;
            left: 0;
            top: 20%;
            bottom: 20%;
            width: 3px;
            border-radius: 0 4px 4px 0;
            background: #0057a8;
            box-shadow: 0 0 8px rgba(0, 87, 168, 0.5);
            opacity: 0;
            transform: scaleY(0);
            transition: opacity 0.2s ease,
              transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          }

          .item-icon-wrapper {
            width: 32px;
            height: 32px;
            border-radius: 8px;
            background: #f1f5f9;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 12px;
            flex-shrink: 0;
            transition: background 0.25s ease,
              transform 0.25s cubic-bezier(0.16, 1, 0.3, 1),
              box-shadow 0.25s ease;

            .v-icon {
              color: #0057a8 !important;
              transition: transform 0.25s ease;
            }
          }

          .item-text-group {
            flex: 1;
            min-width: 0;
            transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);

            .item-title {
              font-size: 13px;
              font-weight: 600;
              line-height: 1.35;
              display: block;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              letter-spacing: 0.1px;
            }
          }

          .item-arrow {
            opacity: 0;
            color: #0057a8 !important;
            transform: translateX(-6px);
            transition: opacity 0.2s ease,
              transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          }

          &:hover {
            background: rgba(0, 87, 168, 0.05);
            color: #0057a8;

            .item-left-indicator {
              opacity: 1;
              transform: scaleY(1);
            }

            .item-icon-wrapper {
              background: rgba(0, 87, 168, 0.12);
              transform: scale(1.08);
              box-shadow: 0 2px 8px rgba(0, 87, 168, 0.15);

              .v-icon {
                transform: rotate(-4deg);
              }
            }

            .item-text-group {
              transform: translateX(4px);
            }

            .item-arrow {
              opacity: 1;
              transform: translateX(0);
            }
          }

          &.router-link-exact-active,
          &.router-link-active {
            background: linear-gradient(
              135deg,
              #0057a8 0%,
              #0284c7 100%
            ) !important;
            color: #ffffff !important;
            font-weight: 700;
            box-shadow: 0 4px 14px rgba(0, 87, 168, 0.25);

            .item-left-indicator {
              opacity: 1;
              transform: scaleY(1);
              background: #ffffff;
              box-shadow: 0 0 8px rgba(255, 255, 255, 0.8);
            }

            .item-icon-wrapper {
              background: rgba(255, 255, 255, 0.22) !important;
              box-shadow: none;

              .v-icon {
                color: #ffffff !important;
              }
            }

            .item-text-group {
              .item-title {
                color: #ffffff;
              }
            }

            .item-arrow {
              opacity: 1;
              color: #ffffff !important;
              transform: translateX(0);
            }
          }
        }
      }
    }
  }

  /* ---------------- MOBILE MENU ACCORDION DRAWER ---------------- */
  .mobile-menu {
    display: none;
    position: absolute;
    top: 56px;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(226, 232, 240, 0.85);
    z-index: 1000;
    box-shadow: 0 16px 36px rgba(15, 23, 42, 0.15);
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.35s cubic-bezier(0.16, 1, 0.3, 1);

    &.active {
      display: block;
      max-height: calc(100vh - 60px);
      overflow-y: auto;
    }

    .mobile-menu-inner {
      padding: 16px;
    }

    .mobile-dropdown {
      margin-bottom: 8px;
      border-radius: 12px;
      overflow: hidden;
      background: #f8fafc;
      border: 1px solid #e2e8f0;

      .mobile-section-title {
        padding: 14px 16px;
        color: #0f172a;
        font-weight: 700;
        font-size: 13.5px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;

        .mobile-title-left {
          display: flex;
          align-items: center;
        }

        .mobile-chevron {
          color: #64748b !important;
          transition: transform 0.25s ease;
        }
      }

      .mobile-section-content {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.3s ease;
        background: #ffffff;

        &.expanded {
          max-height: 500px;
        }

        .mobile-menu-item {
          display: flex;
          align-items: center;
          padding: 12px 16px 12px 32px;
          text-decoration: none;
          color: #334155;
          font-size: 13px;
          font-weight: 600;

          .v-icon {
            margin-right: 12px;
            color: #0057a8 !important;
          }

          &.router-link-exact-active,
          &.router-link-active {
            background: linear-gradient(135deg, #0057a8 0%, #0284c7 100%);
            color: #ffffff !important;
            font-weight: 700;

            .v-icon {
              color: #ffffff !important;
            }
          }
        }
      }
    }
  }

  /* ---------------- USER PROFILE POD & LOGOUT ACTION ---------------- */
  .user-section {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 100%;
    z-index: 1;

    .profile-pod {
      display: flex;
      align-items: center;
      gap: 10px;
      background: rgba(248, 250, 252, 0.85);
      border: 1px solid rgba(226, 232, 240, 0.9);
      border-radius: 24px;
      padding: 4px 12px 4px 4px !important;
      height: 44px !important;
      min-width: 0 !important;
      text-transform: none !important;
      letter-spacing: normal !important;
      transition: all 0.25s ease;

      &:hover {
        background: #ffffff;
        border-color: rgba(0, 87, 168, 0.3);
        box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
      }

      .user-avatar {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        .avatar-box {
          border: 2px solid #0057a8;
          background: linear-gradient(135deg, #0057a8 0%, #0284c7 100%);
          box-shadow: 0 2px 8px rgba(0, 87, 168, 0.2);

          .profile-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .initials-text {
            font-size: 13px;
            font-weight: 800;
            color: #ffffff;
            letter-spacing: 0.5px;
          }
        }

        .status-online-dot {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: #10b981;
          border: 2px solid #ffffff;

          .status-ping {
            position: absolute;
            inset: -2px;
            border-radius: 50%;
            border: 1.5px solid #10b981;
            animation: statusPulse 2s infinite;
          }
        }
      }

      .user-info {
        display: flex;
        flex-direction: row;
        align-items: center;

        .user-id {
          color: #0f172a;
          font-weight: 700;
          font-size: 13px;
          max-width: 120px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          letter-spacing: 0.1px;

          @media (max-width: 860px) {
            display: none;
          }
        }

        .profile-chevron-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          min-height: 20px;
          cursor: pointer;
          pointer-events: auto;
          transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1) !important;
          &.rotate-180 {
            transform: rotate(180deg) !important;
          }
        }
      }
    }

    .logout-btn {
      background: rgba(254, 242, 242, 0.9) !important;
      border: 1px solid rgba(254, 202, 202, 0.8) !important;
      color: #dc2626 !important;
      font-weight: 700 !important;
      font-size: 12px !important;
      border-radius: 10px !important;
      padding: 0 14px !important;
      height: 36px !important;
      text-transform: none !important;
      letter-spacing: 0.2px !important;
      box-shadow: 0 2px 6px rgba(220, 38, 38, 0.08) !important;
      transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1) !important;

      .logout-icon {
        color: #dc2626 !important;
        transition: transform 0.25s ease !important;
      }

      &:hover {
        background: #dc2626 !important;
        color: #ffffff !important;
        border-color: #dc2626 !important;
        box-shadow: 0 4px 14px rgba(220, 38, 38, 0.25) !important;

        .logout-icon {
          color: #ffffff !important;
          transform: translateX(2px);
        }
      }

      @media (max-width: 860px) {
        padding: 0 10px !important;
        span {
          display: none;
        }
        .logout-icon {
          margin-right: 0 !important;
        }
      }
    }

    .change-password-btn {
      background: rgba(238, 242, 255, 0.9) !important;
      border: 1px solid rgba(199, 210, 254, 0.8) !important;
      color: #4f46e5 !important;
      font-weight: 700 !important;
      font-size: 12px !important;
      border-radius: 10px !important;
      padding: 0 14px !important;
      height: 36px !important;
      text-transform: none !important;
      letter-spacing: 0.2px !important;
      box-shadow: 0 2px 6px rgba(79, 70, 229, 0.08) !important;
      transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1) !important;

      .change-password-icon {
        color: #4f46e5 !important;
        transition: transform 0.25s ease !important;
      }

      &:hover {
        background: #4f46e5 !important;
        color: #ffffff !important;
        border-color: #4f46e5 !important;
        box-shadow: 0 4px 14px rgba(79, 70, 229, 0.25) !important;

        .change-password-icon {
          color: #ffffff !important;
          transform: rotate(45deg);
        }
      }

      @media (max-width: 860px) {
        padding: 0 10px !important;
        span {
          display: none;
        }
        .change-password-icon {
          margin-right: 0 !important;
        }
      }
    }
  }
}

.profile-menu-dropdown-content {
  z-index: 99999 !important;
}
</style>